import { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { cn } from 'components/UI/cn'
import {
  CampaignColumnLifecycleStatus,
  CampaignColumnValue,
  deriveCellState,
} from 'services/campaignColumns'

interface Props {
  value?: CampaignColumnValue
  columnStatus?: CampaignColumnLifecycleStatus
  showLoadingForEmpty?: boolean
  isManualColumn?: boolean
  spreadsheetEditDisabled?: boolean
}

export function AICellRenderer({
  value,
  columnStatus,
  showLoadingForEmpty = false,
  isManualColumn = false,
  spreadsheetEditDisabled = false,
}: Props) {
  const text = value?.value?.value ?? ''
  const hasTextValue = text.trim().length > 0
  const state = deriveCellState(value)
  const editTitle = spreadsheetEditDisabled
    ? 'Editing is unavailable while this column is recomputing.'
    : 'Select this cell, then press Enter to edit.'

  const renderEditTrigger = (
    children: ReactNode,
    className: string,
    title = editTitle
  ) => (
    <span className={cn(className, 'cursor-cell')} title={title}>
      {children}
    </span>
  )

  const loadingIndicator = (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-fg-secondary">
      <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
      <span className="truncate">Computing</span>
    </span>
  )

  if (state === 'empty') {
    if (isManualColumn) {
      return renderEditTrigger(
        'Needs value',
        'rounded bg-warning-bg-subtle px-1.5 py-0.5 text-xs text-warning-strong hover:bg-warning-bg',
        'Enter this value manually before using this variable in an email.'
      )
    }
    // Once the column is APPROVED, a missing value row means the celery task
    // hasn't written its result yet. Show the computing spinner instead of a dash.
    if (!value && (columnStatus === 'APPROVED' || showLoadingForEmpty)) {
      return loadingIndicator
    }
    if (value) {
      return renderEditTrigger(
        'Needs value',
        'rounded bg-warning-bg-subtle px-1.5 py-0.5 text-xs text-warning-strong hover:bg-warning-bg',
        'Could not find a value. Enter this manually before using this variable in an email.'
      )
    }
    return renderEditTrigger(
      '—',
      'text-xs text-fg-secondary hover:text-fg-primary'
    )
  }

  if (state === 'failed') {
    const errorMsg = value?.error ?? 'Compute failed'
    return renderEditTrigger(
      'Needs value',
      'rounded bg-warning-bg-subtle px-1.5 py-0.5 text-xs text-warning-strong hover:bg-warning-bg',
      `Could not find a value. Enter this manually before using this variable in an email. ${errorMsg}`
    )
  }

  if (!hasTextValue) {
    return renderEditTrigger(
      'Needs value',
      'rounded bg-warning-bg-subtle px-1.5 py-0.5 text-xs text-warning-strong hover:bg-warning-bg',
      'Enter this value manually before using this variable in an email.'
    )
  }

  return (
    <FloatingTooltip text={text} placement="top" delay={300}>
      {renderEditTrigger(
        <>
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              state === 'edited' && !isManualColumn && 'bg-warning-bg px-1'
            )}
          >
            {text}
          </span>
          {state === 'computed' && !isManualColumn && (
            <span className="ml-1 hidden shrink-0 rounded border border-neutral-200 bg-white px-1 text-xxs uppercase text-neutral-700 group-hover:inline">
              AI
            </span>
          )}
        </>,
        'group flex w-full min-w-0 items-center gap-1 text-left'
      )}
    </FloatingTooltip>
  )
}
